/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import 通用女声 from '@/assets/img/tongyongnvsheng.jpeg';
import INTELLIGENT_ASSISTANT from '@/assets/img/INTELLIGENT_ASSISTANT.png';
import VIRTUAL_GIRL_FRIEND from '@/assets/img/VIRTUAL_GIRL_FRIEND.png';
import TRANSLATE from '@/assets/img/TRANSLATE.png';
import CHILDREN_ENCYCLOPEDIA from '@/assets/img/CHILDREN_ENCYCLOPEDIA.png';
import TEACHING_ASSISTANT from '@/assets/img/TEACHING_ASSISTANT.png';
import CUSTOMER_SERVICE from '@/assets/img/CUSTOMER_SERVICE.png';
import SCREEN_READER from '@/assets/img/SCREEN_READER.png';

export enum ModelSourceType {
  Custom = 'Custom',
  Available = 'Available',
}

export enum CustomParamsType {
  TTS = 'TTS',
  ASR = 'ASR',
  LLM = 'LLM',
}

export enum MODEL_MODE {
  ORIGINAL = 'original',
  VENDOR = 'vendor',
  COZE = 'coze',
}

/**
 * @brief AI 音色可选值
 * @default 通用女声
 * @notes 通用女声、通用男声为默认音色, 其它皆为付费音色。
 *        音色 ID 可于 https://console.volcengine.com/speech/service/8?s=g 中开通获取。
 *        对应 "音色详情" 中, "Voice_type" 列的值。
 */
export enum VOICE_TYPE {
  '通用女声' = 'BV001_streaming'
}

export const VOICE_INFO_MAP = {
  [VOICE_TYPE['通用女声']]: {
    description: '女声 青年 语音合成 通用场景',
    url: '',
    icon: 通用女声,
  }
};

/**
 * @brief TTS 的 Cluster
 */
export enum TTS_CLUSTER {
  TTS = 'volcano_tts',
  MEGA = 'volcano_mega',
  ICL = 'volcano_icl',
}

/**
 * @brief TTS 的 Cluster Mapping
 */
export const TTS_CLUSTER_MAP = {
  ...(Object.keys(VOICE_TYPE).reduce(
    (map, type) => ({
      ...map,
      [type]: TTS_CLUSTER.TTS,
    }),
    {}
  ) as Record<VOICE_TYPE, TTS_CLUSTER>),
};

/**
 * @brief 模型可选值
 * @default SKYLARK_LITE_PUBLIC
 */
export enum AI_MODEL {
  DOUBAO_LITE_4K = 'Doubao-lite-4k',
  DOUBAO_PRO_4K = 'Doubao-pro-4k',
  DOUBAO_PRO_32K = 'Doubao-pro-32k',
  DOUBAO_PRO_128K = 'Doubao-pro-128k',
  VISION = 'Vision',
  ARK_BOT = 'ArkBot',
}

/**
 * @brief 模型来源
 */
export enum AI_MODEL_MODE {
  CUSTOM = 'CustomLLM',
  ARK_V3 = 'ArkV3',
}

/**
 * @brief 各模型对应的模式
 */
export const AI_MODE_MAP: Partial<Record<AI_MODEL, AI_MODEL_MODE>> = {
  [AI_MODEL.DOUBAO_LITE_4K]: AI_MODEL_MODE.ARK_V3,
  [AI_MODEL.DOUBAO_PRO_4K]: AI_MODEL_MODE.ARK_V3,
  [AI_MODEL.DOUBAO_PRO_32K]: AI_MODEL_MODE.ARK_V3,
  [AI_MODEL.DOUBAO_PRO_128K]: AI_MODEL_MODE.ARK_V3,
  [AI_MODEL.VISION]: AI_MODEL_MODE.ARK_V3,
  [AI_MODEL.ARK_BOT]: AI_MODEL_MODE.ARK_V3,
};

/**
 * @brief 方舟模型的 ID
 * @note 具体的模型 ID 请至 https://console.volcengine.com/ark/region:ark+cn-beijing/endpoint?config=%7B%7D&s=g 参看/创建
 *       模型 ID 即接入点 ID, 在上述链接中表格内 "接入点名称" 列中, 类似于 "ep-2024xxxxxx-xxx" 格式即是模型 ID。
 */
export const ARK_V3_MODEL_ID: Partial<Record<AI_MODEL, string>> = {
  [AI_MODEL.DOUBAO_LITE_4K]: 'ep-20250801134236-l597q',
  [AI_MODEL.DOUBAO_PRO_4K]: 'ep-20250801134236-l597q',
  [AI_MODEL.DOUBAO_PRO_32K]: 'ep-20250801134236-l597q',
  [AI_MODEL.DOUBAO_PRO_128K]: 'ep-20250801134236-l597q',
  [AI_MODEL.VISION]: 'ep-20250801134236-l597q',
  // ... 可根据所开通的模型进行扩充
};

/**
 * @brief 方舟智能体 BotID
 * @note 具体的智能体 ID 请至 https://console.volcengine.com/ark/region:ark+cn-beijing/assistant?s=g 参看/创建
 *       Bot ID 即页面上的应用 ID, 类似于 "bot-2025xxxxxx-xxx" 格式即是应用 ID。
 */
export const LLM_BOT_ID: Partial<Record<AI_MODEL, string>> = {
  [AI_MODEL.ARK_BOT]: '************** 此处填充方舟上的 Bot ID *************',
  // ... 可根据所开通的模型进行扩充
};

export enum SCENE {
  INTELLIGENT_ASSISTANT = 'INTELLIGENT_ASSISTANT',
  TEACHING_ASSISTANT = 'TEACHING_ASSISTANT',
  SCREEN_READER = 'SCREEN_READER',
}

export const ScreenShareScene = [SCENE.SCREEN_READER,SCENE.INTELLIGENT_ASSISTANT];

export const Icon = {
  [SCENE.INTELLIGENT_ASSISTANT]: INTELLIGENT_ASSISTANT,
  [SCENE.TEACHING_ASSISTANT]: TEACHING_ASSISTANT,
  [SCENE.SCREEN_READER]: SCREEN_READER,
};
export const Name = {
  [SCENE.INTELLIGENT_ASSISTANT]: '智能助手',
  [SCENE.TEACHING_ASSISTANT]: '课后助教',
  [SCENE.SCREEN_READER]: '读屏助手',
};
/**
 * @brief 智能体启动后的欢迎词。
 */
export const Welcome = {
  [SCENE.INTELLIGENT_ASSISTANT]: '',
  [SCENE.TEACHING_ASSISTANT]: '',
  [SCENE.SCREEN_READER]: '',
};

export const Model = {
  [SCENE.INTELLIGENT_ASSISTANT]: AI_MODEL.DOUBAO_PRO_32K,
  [SCENE.TEACHING_ASSISTANT]: AI_MODEL.VISION,
  [SCENE.SCREEN_READER]: AI_MODEL.VISION,
};

export const Voice = {
  [SCENE.INTELLIGENT_ASSISTANT]: VOICE_TYPE.通用女声,
  [SCENE.TEACHING_ASSISTANT]: VOICE_TYPE.通用女声,
  [SCENE.SCREEN_READER]: VOICE_TYPE.通用女声,
};

/**
 * @brief 大模型 System 角色预设指令，可用于控制模型输出, 类似 Prompt 的概念。
 */
export const Prompt = {
  [SCENE.INTELLIGENT_ASSISTANT]: `##人设
你是一个全能智能体，拥有丰富的百科知识，可以为人们答疑解惑，解决问题。
你性格很温暖，喜欢帮助别人，非常热心。

##技能
1. 当用户询问某一问题时，利用你的知识进行准确回答。回答内容应简洁明了，易于理解。
2. 当用户想让你创作时，比如讲一个故事，或者写一首诗，你创作的文本主题要围绕用户的主题要求，确保内容具有逻辑性、连贯性和可读性。除非用户对创作内容有特殊要求，否则字数不用太长。
3. 当用户想让你对于某一事件发表看法，你要有一定的见解和建议，但是也要符合普世的价值观。`,
  [SCENE.TEACHING_ASSISTANT]: `##人设
你是一个助教，擅长理解【用户问题】，并结合【图片】的信息，来为用户解答各种问题。

##技能
- 用户会将视频中的某些视频帧截为图片送给你，如果用户询问与视频和图片有关的问题，请结合【图片】信息和【用户问题】进行回答；
- 如果用户询问与视频和图片无关的问题，无需描述【图片】内容，直接回答【用户问题】；
- 如果用户给你看的是学科题目，不需要把图片里的文字内容一个一个字读出来，只需要总结一下【图片】里的文字内容，然后直接回答【用户问题】，可以补充一些解题思路；

##约束
- 回答问题要简明扼要，避免复杂冗长的表述，尽量不超过50个字；
- 回答中不要有“图片”、“图中”等相关字眼；`,
  [SCENE.SCREEN_READER]: `##人设
你是人们的 AI 伙伴，可以通过 【屏幕共享实时解析】+【百科知识】来为人们提供服务。

##技能
1. 实时理解屏幕中的内容，包括图片、文字、窗口焦点，自动捕捉光标轨迹；
2. 拥有丰富的百科知识；
3. 如果用户询问与视频和图片有关的问题，请结合【屏幕共享实时解析】的内容、你的【知识】和【用户问题】进行回答；

##风格
语言风格可以随着屏幕内容和用户需求调整，可以是幽默搞笑的娱乐解说，也可以是严谨硬核的技术分析。
- 如果屏幕内容是娱乐节目、动画、游戏等，语言风格偏幽默、活波一些，可以使用夸张的比喻、流行梗、弹幕互动式语言；
- 如果屏幕内容是办公软件、新闻、文章等，语言风格偏专业、正经一些。

## 约束
不要有任何特殊标点符号和任何 Markdown 格式输出，例如 *，# 等。
`
};

export const isVisionMode = (model?: AI_MODEL) => model?.startsWith('Vision');
